/**
 * Request/response models for the WLB Admin API.
 * Source: /api/v1/Admin/** in the API document (swagger).
 *
 * Request interfaces deliberately carry the same names as the API schemas so
 * `tools/check-api-contract.mjs` can compare their fields field-by-field. Every
 * admin request schema is `additionalProperties: false`, so an unknown field is
 * a 400.
 */
import type {
  ModerationCategory,
  ModerationReviewAction,
  ModerationSeverity,
  ModerationStage,
  ModerationStatus,
  ModerationSurface,
  ModerationTargetType,
} from '../moderation';

/** UserRole enum from the API document. */
export type AdminUserRole = 'Admin' | 'BlogAdmin' | 'User' | 'Influencer' | 'AIAgent';

/** Roles allowed to sign in to the admin control panel. */
export const ADMIN_PANEL_ROLES: readonly AdminUserRole[] = ['Admin', 'BlogAdmin'];

// ─── Shared response envelopes ───────────────────────────────────────────────

export interface AdminPagedResult<T> {
  items?: T[] | null;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  message?: string | null;
}

/** Gender enum from the API document, used by the admin user update payload. */
export type AdminGender = 'Male' | 'Female' | 'Other' | 'PreferNotToSay';

export const ADMIN_GENDERS: readonly AdminGender[] = [
  'Male',
  'Female',
  'Other',
  'PreferNotToSay',
];

/**
 * One sign-in attempt from `AdminUserDetailsResponse.loginInfo`. `result`
 * distinguishes a successful sign-in from a failed one, so the history doubles
 * as a lockout trail.
 *
 * `ipAddress` and `deviceId` used to be one field: the procedure aliased the
 * device identifier as `ipAddress`, so the panel's "IP address" column never
 * showed an IP. They are now reported separately, and `ipAddress` is empty for
 * sessions recorded before the column started being populated — a blank IP on
 * an old row is expected, not missing data.
 */
export interface LoginInfoItem {
  loginTimestamp?: string | null;
  loggedOutTimestamp?: string | null;
  ipAddress?: string | null;
  /** The value that used to arrive mislabelled as `ipAddress`. */
  deviceId?: string | null;
  userAgent?: string | null;
  mins?: number | null;
  result?: string | null;
}

/**
 * The admin user record. `password`/`pwd` are part of the API schema but are
 * deliberately not declared: the panel must never read or display them.
 */
export interface AdminUserDetailsResponse {
  row?: number | null;
  userId: number;
  userGUID: string;
  userName?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: AdminUserRole;
  hasReadOnlyAccess?: boolean;
  gender?: string | null;
  aboutMe?: string | null;
  dob?: string | null;
  age?: number | null;
  picture?: string | null;
  country?: string | null;
  joiningDate?: string | null;
  status?: string | null;
  userStatusId?: number | null;
  isAdmin?: boolean;
  isEmailVerified: boolean;
  isUserBlocked: boolean;
  isActive: boolean;
  updatedOn?: string | null;
  lastLoginOn?: string | null;
  lastloginIp?: string | null;
  loginInfo?: LoginInfoItem[] | null;
  firstpostdate?: string | null;
  lastpostdate?: string | null;
  totalpost?: number | null;
  totalvisits?: number | null;
}

export interface AdminUserOperationResponse {
  statusCode: number;
  success: boolean;
  message?: string | null;
  user?: AdminUserDetailsResponse | null;
}

/**
 * `GET /api/v1/Admin/User/{userGuid}` nests the user under `userInfo`, unlike
 * the AdminAccount route, which answers the details object directly.
 */
export interface AdminUserDetailsWrapperResponse {
  status?: string | null;
  statusCode: number;
  message?: string | null;
  userInfo?: AdminUserDetailsResponse | null;
}

export type AdminUserDetailsResponsePagedResult =
  AdminPagedResult<AdminUserDetailsResponse>;

// ─── Users ───────────────────────────────────────────────────────────────────

/**
 * The schema carries no free-text search field: names and email are separate
 * filters, so a single search box has to pick one (the panel searches
 * `userName`, as the statistics list does).
 */
export interface AdminUserListRequest {
  userName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  joiningDate?: string | null;
  timezoneOffset?: number | null;
  isActive?: boolean | null;
  isUserBlocked?: boolean | null;
  isEmailVerified?: boolean | null;
  pageNumber?: number | null;
  pageSize?: number | null;
}

export interface AdminUserUpdateRequest {
  email?: string | null;
  userName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  mobileNo?: string | null;
  isActive?: boolean | null;
  isUserBlocked?: boolean | null;
  isEmailVerified?: boolean | null;
  makeAdmin?: boolean | null;
  gender?: AdminGender | null;
  aboutMe?: string | null;
}

export interface AdminUserActiveStatusRequest {
  userGUID: string;
  isActive: boolean;
}

export interface SetTemporaryPasswordRequest {
  userGuid: string;
  newPassword: string;
}

export interface AdminUserStatisticsListRequest {
  pageNumber?: number | null;
  pageSize?: number | null;
  userName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  joiningDate?: string | null;
  dateFilterType?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  sortColumn?: string | null;
  sortDir?: string | null;
  timezoneOffset?: number | null;
}

export interface AdminUserStatisticsResponse {
  userId: number;
  userName?: string | null;
  joiningDate?: string | null;
  picture?: string | null;
  likes: number;
  posts: number;
  logins: number;
  comments: number;
  shares: number;
  totalPoints: number;
  userRank: number;
  isActive: boolean;
}

export interface AdminUserStatisticsListResponse {
  statusCode: number;
  message?: string | null;
  success: boolean;
  totalCount: number;
  users?: AdminUserStatisticsResponse[] | null;
}

// ─── Admin accounts ──────────────────────────────────────────────────────────

export interface AdminAccountListRequest {
  searchTerm?: string | null;
  isActive?: boolean | null;
  pageNumber?: number | null;
  pageSize?: number | null;
}

export interface AdminAccountCreateRequest {
  firstName?: string | null;
  userName?: string | null;
  email?: string | null;
  password?: string | null;
}

export interface AdminAccountUpdateRequest {
  firstName?: string | null;
  userName?: string | null;
  email?: string | null;
  password?: string | null;
}

export interface AdminAccountActiveStatusRequest {
  userGuid: string;
  isActive: boolean;
}

// ─── Dashboard metrics ───────────────────────────────────────────────────────

/**
 * Body of GetDashboardNewMetrics — the API schema is named `Dashboard`, which
 * would read as a domain model here.
 */
export interface DashboardMetricsRequest {
  startDate: string;
  endDate: string;
}

export interface DashboardMetric {
  metrickey?: string | null;
  metricname?: string | null;
  desktop: number;
  android: number;
  ios: number;
}

export interface DashboardNew {
  userLoggedInDateRange: number;
  newUsersInDateRange: number;
  totalPostsInDateRange: number;
  uniquePostsInDateRange: number;
  returnUsersInDateRange: number;
  timeOnlineInDateRange?: string | null;
  allUsers: number;
  activeUsers: number;
  usersloggedIntoday: number;
  usersloggedInYesterday: number;
  usersloggedInthisMonth: number;
  usersLoggedinpast30days: number;
  totalposts: number;
  uniqueposts: number;
  newusers: number;
  returnusers: number;
  totalcomments: number;
  totalshares: number;
  totalUploadPhotosInDateRange: number;
  metrics?: DashboardMetric[] | null;
}

// ─── Reported content moderation ─────────────────────────────────────────────

export interface AdminReportedPostsFetchRequest {
  userId: number;
  startIndex: number;
  pageSize: number;
  timezoneOffset: number;
  isReviewed?: boolean | null;
}

export interface AdminBulkReportDetailItemDto {
  reportedPostID: number;
  postId: number;
  commentId: number;
}

export interface AdminBulkUpdateReportedPostRequest {
  isReportApproved: boolean;
  reviewerComments?: string | null;
  reportDetails?: AdminBulkReportDetailItemDto[] | null;
}

export interface BulkUpdatePostRequest {
  postIds?: number[] | null;
  isActive: boolean;
}

/** Author/reporter block carried by every reported-post row. */
export interface ApiReportedContentProfile {
  userId?: number | null;
  userGUID?: string | null;
  userName?: string | null;
  userPhoto?: string | null;
  reportedTime?: string | null;
  isUserActive?: boolean | null;
}

/**
 * One reported post. A report against a *comment* also arrives as one of these
 * rows, with `reportedCommentId` set and the comment body carried separately in
 * `ApiReportedContentData.comments`.
 */
export interface ApiReportedContentItem {
  rowNumber?: number | null;
  /** Post id. Named after the legacy "share" the feed was built on. */
  shareId?: number | null;
  title?: string | null;
  postDescription?: string | null;
  postDate?: string | null;
  createdDateTime?: string | null;
  /** Content type name: Text, Photo, Video, Blog… */
  type?: string | null;
  photoAlbum?: string[] | null;
  likeCount?: number | null;
  commentCount?: number | null;
  isPostActive?: boolean | null;
  isCommentsTurnedOff?: boolean | null;
  reportedPostId?: number | null;
  reportedTime?: string | null;
  isReportReviewed?: boolean | null;
  isReportApproved?: boolean | null;
  /** The reporter's own note — what the UI shows as the reason. */
  reportedComments?: string | null;
  reportedCommentId?: number | null;
  reviewerComments?: string | null;
  author?: ApiReportedContentProfile | null;
  reporter?: ApiReportedContentProfile | null;
}

/**
 * A comment on a reported post. This collection is thread *context* (the latest
 * comments of each reported post), not a queue of reported comments: a reported
 * comment is the row in `posts` whose `reportedCommentId` matches `commentId`.
 */
export interface ApiReportedContentComment {
  commentId?: number | null;
  postId?: number | null;
  commentMessage?: string | null;
  createdDateTime?: string | null;
  userId?: number | null;
  userName?: string | null;
  userPhoto?: string | null;
  parentCommentId?: number | null;
  likeCount?: number | null;
  commentType?: string | null;
  /** Single media URL, not a list. */
  images?: string | null;
}

/** `data` of GetReportedPosts. */
export interface ApiReportedContentData {
  totalRecords?: number | null;
  posts?: ApiReportedContentItem[] | null;
  comments?: ApiReportedContentComment[] | null;
}

// ─── Automated content moderation ────────────────────────────────────────────

/**
 * Screening verdicts, not member reports. Every enum on this controller is
 * serialised as its string name, so the payloads are typed with the shared
 * taxonomy unions and read back through its readers — a hand-built numeric
 * value is tolerated on the way in but never produced on the way out.
 *
 * Out-of-range filter values are coerced by the procedure rather than refused,
 * so a request must only ever carry a value from these unions: a typo would
 * silently answer with a different page than the operator asked for.
 */
export interface ContentModerationReviewQueueRequest {
  startIndex?: number;
  pageSize?: number;
  moderationStatus?: ModerationStatus;
  surface?: ModerationSurface | null;
  severity?: ModerationSeverity | null;
  category?: ModerationCategory | null;
  timezoneOffset?: number;
}

export interface ContentModerationReviewDecisionRequest {
  targetType?: ModerationTargetType;
  targetId?: number;
  /**
   * No default on purpose: an omitted action arrives as the ordinal 0 and is
   * rejected, instead of silently allowing or rejecting the content.
   */
  reviewAction?: ModerationReviewAction;
  reviewerNote?: string | null;
  addToAllowList?: boolean;
}

export interface ContentModerationSettingToggleRequest {
  settingKey?: string | null;
  settingValue?: string | null;
  isActive?: boolean;
}

/** One guideline a stage scored the content against. */
export interface ApiContentModerationMatchedCategory {
  categoryId?: number | null;
  categoryKey?: string | null;
  guidelineTitle?: string | null;
  severityId?: number | null;
  score?: number | null;
  thresholdApplied?: number | null;
  matchedOnText?: boolean;
  matchedOnImage?: boolean;
  evidenceSnippet?: string | null;
}

export interface ApiContentModerationQueueItem {
  targetType?: string | number | null;
  targetTypeName?: string | null;
  targetId?: number | null;
  surface?: string | number | null;
  moderationStatus?: string | number | null;
  /** The post/comment/message a media target hangs off. */
  parentId?: number | null;
  title?: string | null;
  bodyText?: string | null;
  contentTypeName?: string | null;
  createdAt?: string | null;
  authorUserId?: number | null;
  authorUserGuid?: string | null;
  authorUserName?: string | null;
  authorFirstName?: string | null;
  authorPhoto?: string | null;
  isAuthorActive?: boolean;
  resultId?: number | null;
  stage?: string | number | null;
  provider?: string | null;
  providerModel?: string | null;
  severity?: string | number | null;
  topCategory?: string | number | null;
  topCategoryKey?: string | null;
  topCategoryTitle?: string | null;
  topScore?: number | null;
  policyRationale?: string | null;
  screenedAt?: string | null;
  mediaUrls?: string[] | null;
  matchedCategories?: ApiContentModerationMatchedCategory[] | null;
}

export interface ApiContentModerationQueueResponse {
  totalRecords?: number | null;
  startIndex?: number | null;
  pageSize?: number | null;
  items?: ApiContentModerationQueueItem[] | null;
}

export interface ApiContentModerationSettingOverride {
  settingKey?: string | null;
  settingValue?: string | null;
  description?: string | null;
  isActive?: boolean;
  updatedBy?: string | null;
  updatedAtUtc?: string | null;
}

export interface ApiContentModerationBacklogCount {
  surface?: string | number | null;
  moderationStatus?: string | number | null;
  itemCount?: number | null;
}

export interface ApiContentModerationSettingsResponse {
  settings?: ApiContentModerationSettingOverride[] | null;
  backlog?: ApiContentModerationBacklogCount[] | null;
}

export interface ApiContentModerationUsageBreakdown {
  surface?: string | number | null;
  stage?: string | number | null;
  provider?: string | null;
  providerModel?: string | null;
  callCount?: number | null;
  errorCount?: number | null;
  promptTokens?: number | null;
  cachedPromptTokens?: number | null;
  completionTokens?: number | null;
  totalCostUsd?: number | null;
}

export interface ApiContentModerationBudgetUsage {
  periodStart?: string | null;
  periodEnd?: string | null;
  totalCostUsd?: number | null;
  callCount?: number | null;
  errorCount?: number | null;
  promptTokens?: number | null;
  cachedPromptTokens?: number | null;
  completionTokens?: number | null;
  breakdown?: ApiContentModerationUsageBreakdown[] | null;
}

export interface ApiContentModerationReviewDecisionResponse {
  isSuccess?: boolean;
  message?: string | null;
  newStatus?: string | number | null;
}

// ─── Error log ───────────────────────────────────────────────────────────────

export interface ErrorLogListRequest {
  pageNumber?: number | null;
  pageSize?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  type?: string | null;
  searchKeyword?: string | null;
}

export interface ErrorLogResponse {
  errorId: string;
  requestMethod?: string | null;
  requestUri?: string | null;
  message?: string | null;
  type?: string | null;
  timeUtc: string;
  userName?: string | null;
}

export type ErrorLogResponsePagedResult = AdminPagedResult<ErrorLogResponse>;

// ─── Domain models used by the admin UI ──────────────────────────────────────

/** One row of the sign-in history shown on the member detail screen. */
export interface AdminLoginEvent {
  loginAt: string;
  loggedOutAt: string;
  /** The real client IP. Empty on sessions recorded before it was captured. */
  ipAddress: string;
  /** Device identifier, previously served under `ipAddress`. */
  deviceId: string;
  userAgent: string;
  minutes: number;
  result: string;
}

export interface AdminUser {
  userId: number;
  userGuid: string;
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  role: AdminUserRole | null;
  isEmailVerified: boolean;
  isUserBlocked: boolean;
  isActive: boolean;
  /** Empty when the API sends no gender; not every account sets one. */
  gender: string;
  aboutMe: string;
  dateOfBirth: string;
  age: number | null;
  country: string;
  picture: string;
  joiningDate: string;
  lastLoginOn: string;
  lastLoginIp: string;
  updatedOn: string;
  firstPostDate: string;
  lastPostDate: string;
  totalPosts: number;
  totalVisits: number;
  /** True when the account holds a read-only admin seat. */
  hasReadOnlyAccess: boolean;
  loginHistory: AdminLoginEvent[];
}

export interface AdminPage<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * A report as the moderation queue shows it. `postedBy`/`content`/`likes`
 * describe the *reported* item, so for a comment report they carry the comment
 * and its author, not the post's.
 *
 * `reportedByEmail`, `postedByEmail`, `groupName` and `shares` are part of the
 * model the card renders but are not in the GetReportedPosts contract today;
 * they stay empty until the endpoint sends them.
 */
export interface ReportedContentItem {
  /** Identifier of the report record, used by the bulk review call. */
  reportedPostId: number;
  postId: number;
  commentId: number;
  /** True when the report targets a comment rather than the post itself. */
  isComment: boolean;
  content: string;
  /** Post title; empty for a comment report, which has none. */
  title: string;
  /** Title of the post a reported comment sits under. */
  parentPostTitle: string;
  /** Media of the reported post/comment, resolved against IMAGE_BASE_URL. */
  postImages: string[];
  reason: string;
  reportedBy: string;
  reportedByAvatar: string;
  reportedByEmail: string;
  postedBy: string;
  postedByAvatar: string;
  postedByEmail: string;
  /** When the reported post/comment was originally created. */
  postedOn: string;
  reportedOn: string;
  /** Group name if the post belongs to a group. */
  groupName: string;
  isBlog: boolean;
  likes: number;
  comments: number;
  shares: number;
  isReviewed: boolean;
  /** Only meaningful once reviewed: the report was upheld rather than dismissed. */
  isApproved: boolean;
  /** False once the post is hidden from members. */
  isPostActive: boolean;
  reviewerComments: string;
}

export interface ReportedContentPage {
  items: ReportedContentItem[];
  /** Reports matching the queue, not just this page. */
  totalRecords: number;
}

/**
 * One guideline match behind a verdict. `score` and `thresholdApplied` are the
 * pair that explains the decision, so they are kept nullable: a local-detector
 * match has neither, and showing `0` instead of "not scored" would read as a
 * confident zero.
 */
export interface ModerationSignal {
  /** Null when the row carried no id; `0` would read as a real category. */
  categoryId: number | null;
  categoryKey: string;
  category: ModerationCategory | null;
  guidelineTitle: string;
  severity: ModerationSeverity | null;
  score: number | null;
  thresholdApplied: number | null;
  matchedOnText: boolean;
  matchedOnImage: boolean;
  evidenceSnippet: string;
}

/** A held (or otherwise screened) piece of content, as the review tools see it. */
export interface ModerationQueueItem {
  /**
   * `targetType|targetId`. The API has no row id of its own, and `targetId`
   * alone collides across types — post 12 and comment 12 are both "12".
   */
  key: string;
  targetType: ModerationTargetType | null;
  targetTypeName: string;
  targetId: number;
  /** True when the screened target is media rather than authored text. */
  isMediaTarget: boolean;
  /**
   * True when the ruling lands on `parentId` rather than on `targetId`. Media
   * attached to a post, comment or message behaves this way; a profile picture
   * does not, because it hangs off a member and has no carrier row.
   */
  rulingTargetsParent: boolean;
  parentId: number | null;
  surface: ModerationSurface | null;
  moderationStatus: ModerationStatus | null;
  title: string;
  bodyText: string;
  contentTypeName: string;
  createdAt: string;
  authorUserId: number | null;
  authorUserGuid: string;
  authorUserName: string;
  authorFirstName: string;
  authorPhoto: string;
  isAuthorActive: boolean;
  resultId: number | null;
  stage: ModerationStage | null;
  provider: string;
  providerModel: string;
  severity: ModerationSeverity | null;
  topCategory: ModerationCategory | null;
  topCategoryKey: string;
  topCategoryTitle: string;
  topScore: number | null;
  policyRationale: string;
  screenedAt: string;
  /** Resolved against IMAGE_BASE_URL, ready for an `<img src>`. */
  mediaUrls: string[];
  signals: ModerationSignal[];
}

export interface ModerationQueuePage {
  items: ModerationQueueItem[];
  /** Rows matching the filters, not just this page. */
  totalRecords: number;
  startIndex: number;
  pageSize: number;
}

/**
 * The outcome of a ruling. Deciding the same item twice is a success with
 * "Content is already in the requested state." — routine with two admins on one
 * queue, so `alreadyInState` separates it from a fresh change instead of
 * surfacing as a failure.
 */
export interface ModerationReviewOutcome {
  /** False means the ruling was refused; the service turns that into an error. */
  isSuccess: boolean;
  newStatus: ModerationStatus | null;
  alreadyInState: boolean;
  /** The API's own sentence, kept for diagnostics rather than for copy. */
  message: string;
}

/** A runtime kill switch. The key set is fixed; the API rejects unknown keys. */
export interface ModerationSetting {
  settingKey: string;
  settingValue: string;
  description: string;
  isActive: boolean;
  updatedBy: string;
  updatedAtUtc: string;
}

export interface ModerationBacklogCount {
  surface: ModerationSurface | null;
  moderationStatus: ModerationStatus | null;
  itemCount: number;
}

export interface ModerationSettingsOverview {
  settings: ModerationSetting[];
  backlog: ModerationBacklogCount[];
}

export interface ModerationUsageBreakdown {
  /**
   * Row identity for rendering. Carries the row's position because the grouping
   * fields are all nullable: two rows with no surface, stage or model would
   * otherwise share a key.
   */
  key: string;
  surface: ModerationSurface | null;
  stage: ModerationStage | null;
  provider: string;
  providerModel: string;
  callCount: number;
  errorCount: number;
  promptTokens: number;
  cachedPromptTokens: number;
  completionTokens: number;
  totalCostUsd: number;
}

/**
 * Month-to-date moderation spend. Moderation only: the AI buddy agent bills to
 * a separate project key and is not counted here.
 */
export interface ModerationUsage {
  periodStart: string;
  periodEnd: string;
  totalCostUsd: number;
  callCount: number;
  errorCount: number;
  promptTokens: number;
  cachedPromptTokens: number;
  completionTokens: number;
  breakdown: ModerationUsageBreakdown[];
}
